<script setup lang="ts">
import { computed } from 'vue';

import { sanitizeHtml } from '@/shared/lib';
import { IconChevronLeft, IconEmoji, IconPaperclip, IconPlus } from '@/shared/ui/icons';

import { getProviderLabel } from '../lib/provider-labels';
import MessengerProviderIcon from './MessengerProviderIcon.vue';

const props = defineProps<{
  /** Провайдер в любом регистре: telegram | max | vk. */
  provider: string;
  /** Текст сообщения; теги разметки допустимы, их чистит DOMPurify. */
  body: string;
  /** Название чата — так клиент видит отправителя. */
  title: string;
  /** Подпись inline-кнопки под сообщением; без неё кнопка не рисуется. */
  buttonLabel?: string | null;
  /** Время у пузыря сообщения, `HH:mm`. */
  time: string;
}>();

/** Каждый мессенджер красит ленту по-своему — экран должен быть узнаваем. */
const CHAT_BACKGROUND: Record<string, string> = {
  telegram: 'bg-(--preview-chat-telegram)',
  max: 'bg-(--preview-chat-max)',
  vk: 'bg-(--preview-chat-vk)',
};

const background = computed(() => CHAT_BACKGROUND[props.provider.toLowerCase()] ?? 'bg-(--preview-chat-vk)');
const content = computed(() => sanitizeHtml(props.body));
</script>

<template>
  <div class="w-full max-w-[320px] rounded-[2.5rem] bg-(--preview-device-frame) p-2 shadow-2xl">
    <div class="overflow-hidden rounded-[2rem] bg-(--preview-screen-bg) text-(--preview-screen-text)">
      <div class="flex justify-center pt-2">
        <span class="h-1.5 w-20 rounded-full bg-(--preview-screen-field)" />
      </div>

      <div class="flex items-center gap-2 border-b border-(--preview-screen-divider) px-3 py-2.5">
        <IconChevronLeft
          class="text-(--preview-screen-accent)"
          :width="12"
          :height="12"
        />
        <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-(--preview-screen-field)">
          <MessengerProviderIcon
            :provider="provider"
            :size="16"
          />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-[13px] font-semibold">{{ title }}</span>
          <span class="block text-[11px] text-(--preview-screen-muted)">{{ getProviderLabel(provider) }}</span>
        </span>
      </div>

      <div
        class="min-h-72 space-y-3 px-3 py-4"
        :class="background"
      >
        <p
          class="mx-auto w-fit rounded-full bg-(--preview-screen-bg)/70 px-2.5 py-0.5 text-[11px] text-(--preview-screen-muted)"
        >
          Сегодня
        </p>
        <div class="max-w-[85%] rounded-2xl rounded-tl-md bg-(--preview-screen-bg) px-3 py-2 shadow-sm">
          <!-- Текст очищен DOMPurify в content. -->
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="text-[13px] leading-[1.45] break-words whitespace-pre-line"
            v-html="content"
          />
          <!-- eslint-enable vue/no-v-html -->
          <span class="mt-1 block text-right text-[10px] text-(--preview-screen-muted)">{{ time }}</span>
        </div>

        <div
          v-if="buttonLabel"
          class="max-w-[85%] rounded-xl bg-(--preview-screen-bg) px-3 py-2 text-center text-[13px] font-medium text-(--preview-screen-accent) shadow-sm"
        >
          {{ buttonLabel }}
        </div>
      </div>

      <div class="flex items-center gap-2 border-t border-(--preview-screen-divider) px-3 py-2.5">
        <IconPlus
          class="text-(--preview-screen-muted)"
          :width="14"
          :height="14"
        />
        <span
          class="flex-1 rounded-full bg-(--preview-screen-field) px-3 py-1.5 text-[12px] text-(--preview-screen-muted)"
          >Сообщение</span
        >
        <IconPaperclip
          class="text-(--preview-screen-muted)"
          :width="16"
          :height="16"
        />
        <IconEmoji
          class="text-(--preview-screen-muted)"
          :width="16"
          :height="16"
        />
      </div>
    </div>
  </div>
</template>
